'use client'

import { Cell, Divider } from '@vinted/web-ui'
import { useEffect } from 'react'
import { validate as uuidvalidate } from 'uuid'

import { useLocation } from '@marketplace-web/shared/browser'
import { cookiesDataByName, useCookie } from '@marketplace-web/shared/cookies'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { SeparatedList } from '@marketplace-web/shared/ui-helpers'
import { urlWithParams } from '@marketplace-web/shared/utils'
import {
  clickEvent,
  searchFaqEntriesEvent,
  userClickHelpCenterSearchResult,
} from '_libs/common/event-tracker/events'
import { AccessChannel } from 'constants/access-channel'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import useHelpCenterSessionTracking from 'hooks/useHelpCenterSessionTracking'
import type { HelpCenterQuestionModel } from 'types/models/help-center'

type Props = {
  questions: Array<HelpCenterQuestionModel>
}

const HelpCenterQuestions = ({ questions }: Props) => {
  const { access_channel, search_text, thread_id } = useLocation().searchParams
  const { track } = useTracking()
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const isHcSearch = access_channel === AccessChannel.HcSearch
  const cookies = useCookie()

  useEffect(() => {
    if (!isHcSearch) return
    if (typeof search_text !== 'string') return
    if (!helpCenterSessionId) return

    track(
      searchFaqEntriesEvent({
        searchSessionId: cookies.get(cookiesDataByName.help_center_search_session_id) || null,
        searchQuery: search_text,
        faqEntryIds: questions.map(question => question.id),
        helpCenterSessionId,
      }),
    )
  }, [track, questions, isHcSearch, search_text, helpCenterSessionId, cookies])

  const handleOnClick = (faqEntryId: number) => () => {
    if (!isHcSearch) return

    const targetDetails = {
      search_session_id: cookies.get(cookiesDataByName.help_center_search_session_id) || null,
      faq_entry_id: faqEntryId,
    }

    track(
      clickEvent({
        target: ClickableElement.HelpCenterSearchResult,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )

    if (!!helpCenterSessionId && uuidvalidate(helpCenterSessionId)) {
      track(
        userClickHelpCenterSearchResult({
          helpCenterSessionId,
        }),
      )
    }
  }

  const renderQuestionItem = ({ id, title, path }: HelpCenterQuestionModel) => {
    return (
      <Cell
        key={id}
        title={title}
        type={Cell.Type.Navigating}
        url={urlWithParams(path, {
          thread_id,
        })}
        onClick={handleOnClick(id)}
        chevron
        testId={`help-center-question-${id}`}
      />
    )
  }

  return <SeparatedList separator={<Divider />}>{questions.map(renderQuestionItem)}</SeparatedList>
}

export default HelpCenterQuestions
